import { format, parseISO } from 'date-fns';
import { Area, Bar, BarChart, CartesianGrid, Cell, ComposedChart, Legend, Line, Pie, PieChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { DailyPoint, NameCount } from '@/hooks/useSuperadmin';

import { SERIES } from './utils';
const TOOLTIP = { background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 };

/** Actions on the left axis, unique users on their own right axis so a small user count stays readable. */
export const DailyChart = ({ data, usersLabel = 'Users', height = 288, selectedDay, onSelectDay }: { data: DailyPoint[]; usersLabel?: string; height?: number; selectedDay?: string | null; onSelectDay?: (day: string) => void }) => (
  <div style={{ height }} role="img" aria-label={`Daily actions and ${usersLabel.toLowerCase()}${onSelectDay ? '. Click a day to filter.' : ''}`}>
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={data} margin={{ left: 0, right: 0, top: 8 }} style={onSelectDay ? { cursor: 'pointer' } : undefined}
        onClick={onSelectDay ? (e: { activeLabel?: string | number }) => { if (e?.activeLabel) onSelectDay(String(e.activeLabel)); } : undefined}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
        <XAxis dataKey="date" tickFormatter={(d: string) => format(parseISO(d), 'd MMM')} tick={{ fontSize: 11 }} minTickGap={24} />
        <YAxis yAxisId="actions" allowDecimals={false} tick={{ fontSize: 11 }} width={40} label={{ value: 'Actions', angle: -90, position: 'insideLeft', fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
        <YAxis yAxisId="users" orientation="right" allowDecimals={false} tick={{ fontSize: 11 }} width={40} label={{ value: usersLabel, angle: 90, position: 'insideRight', fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
        <Tooltip labelFormatter={(d: string) => format(parseISO(d), 'EEE d MMM yyyy')} contentStyle={TOOLTIP} />
        <Legend verticalAlign="top" height={28} iconType="plainline" />
        {selectedDay && <ReferenceLine yAxisId="actions" x={selectedDay} stroke={SERIES[0]} strokeWidth={2} strokeDasharray="4 3" label={{ value: format(parseISO(selectedDay), 'd MMM'), position: 'top', fontSize: 11 }} />}
        <Area yAxisId="actions" type="monotone" dataKey="actions" name="Actions" stroke={SERIES[0]} fill={SERIES[0]} fillOpacity={0.15} />
        <Line yAxisId="users" type="monotone" dataKey="users" name={usersLabel} stroke={SERIES[1]} strokeWidth={2} dot={{ r: 2 }} />
      </ComposedChart>
    </ResponsiveContainer>
  </div>
);

/** Horizontal bars; when onSelect is given, clicking a bar (or pressing its button below) drills in. */
export const BarList = ({ data, onSelect, height = 288 }: { data: NameCount[]; onSelect?: (name: string) => void; height?: number }) => (
  <div>
    <div style={{ height }} role="img" aria-label="Usage by screen">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ left: 8, right: 16 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
          <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
          <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
          <Tooltip contentStyle={TOOLTIP} cursor={{ fill: 'hsl(var(--muted))' }} />
          <Bar dataKey="count" name="Uses" fill={SERIES[0]} radius={[0, 4, 4, 0]} cursor={onSelect ? 'pointer' : undefined}
            onClick={onSelect ? (d: { name?: string }) => d?.name && onSelect(d.name) : undefined} />
        </BarChart>
      </ResponsiveContainer>
    </div>
    {onSelect && (
      <ul className="mt-2 flex flex-wrap gap-2" aria-label="Drill into a screen">
        {data.map((d) => (
          <li key={d.name}>
            <button type="button" onClick={() => onSelect(d.name)} className="rounded-full border border-border px-2.5 py-0.5 font-mono text-[11px] hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{d.name}</button>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export const TypesDonut = ({ data, selected, onSelect }: { data: NameCount[]; selected?: string | null; onSelect?: (name: string) => void }) => (
  <div>
    <div className="h-56" role="img" aria-label="Actions by type">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={data} dataKey="count" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={2} cursor={onSelect ? 'pointer' : undefined}
            onClick={onSelect ? (d: { name?: string }) => d?.name && onSelect(d.name) : undefined}>
            {data.map((d, i) => <Cell key={i} fill={SERIES[i % SERIES.length]} fillOpacity={selected && selected !== d.name ? 0.3 : 1} />)}
          </Pie>
          <Tooltip contentStyle={TOOLTIP} />
        </PieChart>
      </ResponsiveContainer>
    </div>
    <ul className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
      {data.slice(0, 6).map((a, i) => {
        const label = <><span className="h-2 w-2 rounded-full" style={{ background: SERIES[i % SERIES.length] }} aria-hidden="true" />{a.name.replace(/_/g, ' ')} · {new Intl.NumberFormat().format(a.count)}</>;
        return (
          <li key={a.name}>
            {onSelect ? (
              <button type="button" aria-pressed={selected === a.name} onClick={() => onSelect(a.name)}
                className={`flex items-center gap-1.5 rounded-full border px-2 py-0.5 hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${selected === a.name ? 'border-primary text-foreground' : 'border-transparent'}`}>{label}</button>
            ) : <span className="flex items-center gap-1.5">{label}</span>}
          </li>
        );
      })}
    </ul>
  </div>
);
